import { collection, doc, getDocs, setDoc, deleteDoc } from 'firebase/firestore'
import type { Table } from '../../types'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'

const tablesCacheByPlace: Record<string, Table[]> = {}

function tablesRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'tables')
}

function tableDoc(placeId: string, tableId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'tables', tableId)
}

export async function primeTablesCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  const snap = await getDocs(tablesRef(placeId))
  tablesCacheByPlace[placeId] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Table))
}

export function getTables(): Table[] {
  const placeId = getCurrentPlaceId()
  return tablesCacheByPlace[placeId] ?? []
}

export function saveTables(tables: Table[]): void {
  const placeId = getCurrentPlaceId()
  tablesCacheByPlace[placeId] = tables
  ;(async () => {
    const snap = await getDocs(tablesRef(placeId))
    const ids = new Set(tables.map((t) => t.id))
    for (const d of snap.docs) {
      if (!ids.has(d.id)) await deleteDoc(d.ref)
    }
    for (const t of tables) {
      await setDoc(tableDoc(placeId, t.id), t)
    }
  })().catch((e) => console.error('Firestore saveTables failed', e))
}

function generateTableId(): string {
  return `T-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addTable(table: Omit<Table, 'id'>): Table {
  const placeId = getCurrentPlaceId()
  const id = generateTableId()
  const full: Table = { ...table, id }
  const cache = tablesCacheByPlace[placeId] ?? []
  tablesCacheByPlace[placeId] = [...cache, full]
  setDoc(tableDoc(placeId, id), full).catch((e) =>
    console.error('Firestore addTable failed', e),
  )
  return full
}

export function updateTable(id: string, patch: Partial<Table>): Table[] {
  const placeId = getCurrentPlaceId()
  const cache = tablesCacheByPlace[placeId] ?? []
  tablesCacheByPlace[placeId] = cache.map((t) => (t.id === id ? { ...t, ...patch } : t))
  const found = tablesCacheByPlace[placeId].find((t) => t.id === id)
  if (found) {
    setDoc(tableDoc(placeId, id), found).catch((e) =>
      console.error('Firestore updateTable failed', e),
    )
  }
  return tablesCacheByPlace[placeId]
}

export function deleteTable(id: string): Table[] {
  const placeId = getCurrentPlaceId()
  const cache = tablesCacheByPlace[placeId] ?? []
  tablesCacheByPlace[placeId] = cache.filter((t) => t.id !== id)
  deleteDoc(tableDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteTable failed', e),
  )
  return tablesCacheByPlace[placeId]
}

export function isTablesPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in tablesCacheByPlace
}

/** Clear in-memory cache so the next user does not see previous account/place data. */
export function clearTablesCache(): void {
  Object.keys(tablesCacheByPlace).forEach((k) => delete tablesCacheByPlace[k])
}
